import { useState, useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card, SectionTitle } from "@/components/dash";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { TIMEZONES } from "@/lib/constants";
import { Loader2, Sparkles, Check, CreditCard, RefreshCw } from "lucide-react";
import { toast } from "sonner";

const CONNECT_ENABLED = true;

const PLANS = [
  { key: "founding_monthly", label: "Founding Baker", price: "$12", cadence: "/mo", note: "For early bakery owners joining during launch.", status: "founding" },
  { key: "standard_monthly", label: "Standard", price: "$19", cadence: "/mo", note: "Everything you need for weekly preorder drops.", status: "standard" },
  { key: "standard_yearly", label: "Standard (yearly)", price: "$180", cadence: "/yr", note: "Two months free versus monthly billing.", status: "standard" },
];

export default function Settings() {
  const { bakery, user, refresh } = useAuth();
  const [form, setForm] = useState({ name: bakery.name, owner_name: bakery.owner_name || "", location: bakery.location || "", timezone: bakery.timezone });
  const [saving, setSaving] = useState(false);
  const [payLoading, setPayLoading] = useState("");
  const [connect, setConnect] = useState(null);
  const [connectLoading, setConnectLoading] = useState(false);
  const billingRef = useRef(null);
  const location = useLocation();

  useEffect(() => {
    if (location.hash === "#billing" && billingRef.current) {
      setTimeout(() => billingRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }), 200);
    }
  }, [location.hash]);

  useEffect(() => {
    if (!CONNECT_ENABLED) return;
    api.get("/payments/connect/status").then((r) => setConnect(r.data)).catch(() => setConnect({ connected: false, charges_enabled: false }));
  }, []);

  const startConnect = async () => {
    setConnectLoading(true);
    try {
      const { data } = await api.post("/payments/connect/start", { origin_url: window.location.origin });
      window.location.href = data.url;
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); setConnectLoading(false); }
  };

  const refreshConnect = async () => {
    try { const { data } = await api.get("/payments/connect/status"); setConnect(data); toast.success("Status refreshed"); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const save = async () => {
    setSaving(true);
    try {
      await api.put("/bakery", { name: form.name, location: form.location, timezone: form.timezone });
      await refresh();
      toast.success("Settings saved");
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const startSubscription = async (lookup_key) => {
    setPayLoading(lookup_key);
    try {
      const { data } = await api.post("/payments/subscription-checkout", { lookup_key, origin_url: window.location.origin });
      window.location.href = data.checkout_url;
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); setPayLoading(""); }
  };

  const isActive = bakery.subscription_active;

  return (
    <div className="space-y-6">
      <SectionTitle>Settings</SectionTitle>

      <Card>
        <h2 className="mb-4 font-serif text-lg text-cocoa">Bakery profile</h2>
        <div className="space-y-4">
          <div><Label className="text-cocoa">Bakery name</Label><Input value={form.name} onChange={(e) => set("name", e.target.value)} className="mt-1.5 rounded-xl bg-cream" data-testid="settings-name" /></div>
          <div><Label className="text-cocoa">Location</Label><Input value={form.location} onChange={(e) => set("location", e.target.value)} className="mt-1.5 rounded-xl bg-cream" data-testid="settings-location" /></div>
          <div>
            <Label className="text-cocoa">Time zone</Label>
            <Select value={form.timezone} onValueChange={(v) => set("timezone", v)}>
              <SelectTrigger className="mt-1.5 rounded-xl bg-cream" data-testid="settings-timezone"><SelectValue /></SelectTrigger>
              <SelectContent>{TIMEZONES.map((t) => <SelectItem key={t} value={t}>{t.replace("_", " ")}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <Button onClick={save} disabled={saving} className="rounded-full bg-terracotta px-8 py-6 text-warmwhite hover:bg-terracotta/90" data-testid="save-settings-btn">{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save"}</Button>
        </div>
      </Card>

      <Card>
        <h2 className="mb-4 font-serif text-lg text-cocoa">Account</h2>
        <div className="grid gap-3 text-sm">
          <div className="flex justify-between border-b border-cocoa/5 pb-2"><span className="text-cocoa/60">Owner</span><span className="text-cocoa">{form.owner_name || user.name}</span></div>
          <div className="flex justify-between border-b border-cocoa/5 pb-2"><span className="text-cocoa/60">Email</span><span className="text-cocoa">{user.email}</span></div>
          <div className="flex justify-between"><span className="text-cocoa/60">Storefront</span><span className="text-cocoa">/bakery/{bakery.slug}</span></div>
        </div>
      </Card>

      {CONNECT_ENABLED && (
      <Card>
        <div className="flex items-start gap-4">
          <span className="flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-full bg-sage/25 text-cocoa"><CreditCard strokeWidth={1.5} className="h-5 w-5" /></span>
          <div className="flex-1">
            <h2 className="font-serif text-lg text-cocoa">Online payments (Stripe)</h2>
            {!connect ? (
              <p className="mt-1 text-sm text-cocoa/60">Checking your Stripe connection…</p>
            ) : connect.charges_enabled ? (
              <p className="mt-1 text-sm text-cocoa/60">Connected — your storefront accepts card payments and payouts go straight to your own Stripe account. You keep 100% of every sale.</p>
            ) : connect.connected ? (
              <p className="mt-1 text-sm text-cocoa/60">Almost there — finish your Stripe onboarding to start accepting online payments on your storefront.</p>
            ) : (
              <p className="mt-1 text-sm text-cocoa/60">Connect your own Stripe account to accept card payments on your storefront. Money goes directly to you — you keep 100% of every sale. Until you connect, customers only see your manual payment instructions.</p>
            )}
          </div>
          {connect?.charges_enabled && <span className="flex items-center gap-1 rounded-full bg-sage/25 px-3 py-1 text-xs font-medium text-cocoa"><Check className="h-3.5 w-3.5" /> Active</span>}
        </div>
        <div className="mt-4 flex flex-wrap gap-2">
          {!connect?.charges_enabled && (
            <Button onClick={startConnect} disabled={connectLoading || !connect} className="rounded-full bg-terracotta text-warmwhite hover:bg-terracotta/90" data-testid="connect-stripe-btn">
              {connectLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <><CreditCard className="mr-1.5 h-4 w-4" /> {connect?.connected ? "Finish Stripe setup" : "Connect Stripe"}</>}
            </Button>
          )}
          {connect?.connected && (
            <Button variant="outline" onClick={refreshConnect} className="rounded-full border-cocoa/20 text-cocoa" data-testid="connect-refresh-btn"><RefreshCw className="mr-1.5 h-4 w-4" /> Refresh status</Button>
          )}
        </div>
      </Card>
      )}

      <div id="billing" ref={billingRef} className="scroll-mt-24">
      <Card>
        <div className="flex items-start gap-4">
          <span className="flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-full bg-terracotta/12 text-terracotta"><Sparkles strokeWidth={1.5} className="h-5 w-5" /></span>
          <div className="flex-1">
            <h2 className="font-serif text-lg text-cocoa">
              {isActive ? <>Active plan: <span className="capitalize text-terracotta">{bakery.billing_status}</span></> : <>Billing status: <span className="capitalize text-terracotta">{bakery.billing_status || "founding"} access</span></>}
            </h2>
            <p className="mt-1 text-sm text-cocoa/60">
              {isActive
                ? "Thank you for subscribing! You can change your plan anytime below."
                : "You're on founding access — everything is unlocked. Subscribe below to support Oven & Order and lock in your plan."}
            </p>
          </div>
        </div>

        <div className="mt-6 grid gap-4 sm:grid-cols-3">
          {PLANS.map((p) => {
            const current = bakery.subscription_plan === p.key;
            return (
              <div key={p.key} className={`rounded-2xl border p-5 ${current ? "border-terracotta bg-terracotta/5" : "border-cocoa/10 bg-cream"}`}>
                <div className="flex items-center justify-between">
                  <p className="font-medium text-cocoa">{p.label}</p>
                  {current && <span className="flex items-center gap-1 text-xs font-medium text-terracotta"><Check className="h-3.5 w-3.5" /> Current</span>}
                </div>
                <p className="mt-2 font-serif text-3xl text-cocoa">{p.price}<span className="text-base text-cocoa/50">{p.cadence}</span></p>
                <p className="mt-2 text-xs text-cocoa/60">{p.note}</p>
                <Button
                  onClick={() => startSubscription(p.key)}
                  disabled={!!payLoading || current}
                  className={`mt-4 w-full rounded-full ${current ? "bg-cocoa/10 text-cocoa/50" : "bg-terracotta text-warmwhite hover:bg-terracotta/90"}`}
                  data-testid={`subscribe-${p.key}`}
                >
                  {payLoading === p.key ? <Loader2 className="h-4 w-4 animate-spin" /> : current ? "Current plan" : <><CreditCard className="mr-1.5 h-4 w-4" /> Subscribe</>}
                </Button>
              </div>
            );
          })}
        </div>
        <p className="mt-4 text-xs text-cocoa/40">Test mode — use card 4242 4242 4242 4242, any future expiry and CVC. No real charges are made.</p>
      </Card>
      </div>
    </div>
  );
}
